'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import EmergencyButton from '@/components/EmergencyButton';

interface LucatHeaderProps {
  showBack?: boolean;
  backHref?: string;
  backLabel?: string;
  screenTitle?: string;
  screenTitleFull?: string; // full title for aria-label, screenTitle is the short display version
  showSettings?: boolean; // show gear icon before clock — for both home modes
}

// Map long titles to short display titles
function getShortTitle(title: string): string {
  if (title === 'Messages et photos') return 'Messages';
  if (title === 'Demander à être appelé') return 'Être appelé';
  return title;
}

// Standalone settings button — flex sibling, never inside EmergencyButton
function HeaderSettingsButton() {
  return (
    <Link href="/reglages-accueil" style={{ textDecoration: 'none', flexShrink: 0 }}>
      <button
        aria-label="Modifier l'affichage de l'accueil"
        title="Paramètres d'affichage"
        style={{
          background: 'rgba(255,255,255,0.12)',
          border: '1.5px solid rgba(255,255,255,0.3)',
          borderRadius: '12px',
          width: '56px',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'background 0.18s ease, border-color 0.18s ease',
          outline: 'none',
        }}
        onFocus={e => {
          (e.currentTarget as HTMLButtonElement).style.outline = '3px solid rgba(255,255,255,0.7)';
          (e.currentTarget as HTMLButtonElement).style.outlineOffset = '2px';
        }}
        onBlur={e => {
          (e.currentTarget as HTMLButtonElement).style.outline = 'none';
        }}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>
    </Link>
  );
}

export default function LucatHeader({
  showBack = false,
  backHref = '/',
  backLabel = 'Accueil',
  screenTitle,
  screenTitleFull,
  showSettings = false,
}: LucatHeaderProps) {
  const [timeStr, setTimeStr] = useState('');
  const [dateStr, setDateStr] = useState('');

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const h = now.getHours().toString().padStart(2, '0');
      const m = now.getMinutes().toString().padStart(2, '0');
      setTimeStr(`${h}:${m}`);

      const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
      const months = [
        'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
        'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
      ];
      setDateStr(`${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`);
    };
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, []);

  const displayTitle = screenTitle ? getShortTitle(screenTitle) : undefined;
  const ariaTitle = screenTitleFull || screenTitle;

  return (
    <header
      className="lucat-header"
      style={{
        flexShrink: 0,
        paddingLeft: 'max(16px, env(safe-area-inset-left))',
        paddingRight: 'max(20px, env(safe-area-inset-right))',
        paddingTop: 'max(0px, env(safe-area-inset-top))',
      }}
    >
      {/* Left side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {showBack && (
          <Link href={backHref} style={{ textDecoration: 'none', flexShrink: 0 }}>
            <button className="nav-back-btn" aria-label={`Retour — ${backLabel}`}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span>{backLabel}</span>
            </button>
          </Link>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flexShrink: 0 }}>
          <AppLogo size={40} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#5DBB1F', fontWeight: 800, fontSize: 'clamp(16px, 1.6vw, 20px)', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                LUCAT
              </span>
              <span style={{ color: '#C0C8D4', fontWeight: 600, fontSize: 'clamp(16px, 1.6vw, 20px)', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                CONNECT
              </span>
            </div>
          </div>
        </div>

        {displayTitle && (
          <div
            aria-label={ariaTitle}
            style={{
              borderLeft: '1.5px solid rgba(255,255,255,0.2)',
              paddingLeft: '16px',
              marginLeft: '4px',
              color: '#FFFFFF',
              fontSize: 'clamp(18px, 2vw, 26px)',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              flex: 1,
            }}
          >
            {displayTitle}
          </div>
        )}

        {!showBack && !displayTitle && (
          <div style={{ marginLeft: '16px', minWidth: 0, overflow: 'hidden' }}>
            <div style={{ color: '#FFFFFF', fontSize: 'clamp(22px, 2.4vw, 30px)', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Bonjour André
            </div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 'clamp(16px, 1.6vw, 20px)', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Que souhaitez-vous faire ?
            </div>
          </div>
        )}
      </div>

      {/* Right side — flex row: [Settings?] [Clock/Date] [Connected] [Emergency] */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        flexShrink: 0,
      }}>
        {/* 1. Settings button — first in right group, before clock */}
        {showSettings && <HeaderSettingsButton />}

        {/* 2. Clock and date */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{
            color: '#FFFFFF',
            fontSize: 'clamp(24px, 2.5vw, 32px)',
            fontWeight: 700,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.02em',
          }}>
            {timeStr}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 'clamp(13px, 1.3vw, 17px)', fontWeight: 500, marginTop: '2px', whiteSpace: 'nowrap' }}>
            {dateStr}
          </div>
        </div>

        {/* 3. Connection status */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(93,187,31,0.2)',
          border: '1.5px solid rgba(93,187,31,0.5)',
          borderRadius: '24px',
          padding: '6px 12px',
          flexShrink: 0,
        }}>
          <span className="status-dot-online" />
          <span style={{ color: '#A8E87C', fontSize: 'clamp(13px, 1.3vw, 15px)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Connecté
          </span>
        </div>

        {/* 4. Emergency button — always last */}
        <EmergencyButton variant="header" />
      </div>
    </header>
  );
}